import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Search, ChevronRight, Loader2, Plus, Users, X, AlertCircle, LayoutList, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/services/api";

const PAGE_SIZE = 25;
const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_PILL = {
  INDIVIDUAL:    "text-sky-400 bg-sky-400/10 border-sky-400/20",
  HOUSEHOLD:     "text-[#5E4DB2] bg-[#F1ECFF] border-[#D8CEF7]",
  ORGANIZATION:  "text-[#8C4B00] bg-amber-400/10 border-amber-400/20",
  CORPORATE:     "text-[#8C4B00] bg-amber-400/10 border-amber-400/20",
};

const BLANK = { name: "", type: "INDIVIDUAL", email: "", phone: "", website: "", notes: "" };
const inp = "w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] focus:outline-none focus:border-[#0176D3]/50 focus:ring-1 focus:ring-[#0176D3]/20";

function CreateAccountModal({ onClose, onCreated }) {  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => { setForm(p => ({...p, [k]: v})); setErrors(p => ({...p, [k]: ""})); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Account name is required";
    if (!form.email.trim() && !form.phone.trim())
      e.contact = "At least one contact method (email or phone) is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/crm/accounts", form);
      toast.success(`Account ${data.account_number || data.name} created`);
      onCreated(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.existing_id) {
        // Duplicate account detected
        setErrors({ name: `An account with this name already exists: "${detail.existing_name}"` });
        toast.warning("Duplicate account detected — see highlighted field.");
      } else if (Array.isArray(detail)) {
        const errs = {};
        detail.forEach(d => { errs[d.field] = d.message; });
        setErrors(errs);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to create account");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="bg-white rounded-xl border border-[#E5E5E5] w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#0176D3]" />
            <h2 className="text-base font-semibold text-[#181818]">New Account</h2>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-[#706E6B]" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Account Name <span className="text-rose-500">*</span></label>
            <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Prashasta Events" data-testid="account-name" className={`${inp}${errors.name ? " border-rose-400" : ""}`} />
            {errors.name && <p data-testid="err-account-name" className="text-xs text-rose-500 mt-1">{errors.name}</p>}
          </div>
          {/* Type */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Account Type</label>
            <select value={form.type} onChange={e => set("type", e.target.value)} className={inp}>
              <option value="INDIVIDUAL">Individual</option>
              <option value="HOUSEHOLD">Household</option>
              <option value="ORGANIZATION">Organization</option>
              <option value="CORPORATE">Corporate</option>
            </select>
          </div>
          {/* Contact row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Email <span className="text-rose-500">*</span></label>
              <input type="email" value={form.email} onChange={e => { set("email", e.target.value); setErrors(p=>({...p, contact:""})); }} placeholder="name@example.com" data-testid="account-email" className={`${inp}${errors.contact ? " border-rose-400" : ""}`} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Phone</label>
              <input value={form.phone} onChange={e => { set("phone", e.target.value); setErrors(p=>({...p, contact:""})); }} placeholder="+91 …" data-testid="account-phone" className={inp} />
            </div>
          </div>
          {errors.contact && <p data-testid="err-account-contact" className="text-xs text-rose-500 -mt-2">{errors.contact}</p>}
          {/* Website */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Website</label>
            <input value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://…" className={inp} />
          </div>
          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional context…" className={`${inp} resize-none`} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#706E6B] hover:text-[#181818]">Cancel</button>
          <button data-testid="account-submit-btn" onClick={submit} disabled={saving} className="px-4 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#0060B0] disabled:opacity-50">
            {saving ? "Saving…" : "Create Account"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditAccountModal({ account, onClose, onUpdated }) {
  const original = {
    name:     account.name || account.customer_name || "",
    type:     account.customer_type || "INDIVIDUAL",
    email:    account.email || account.contact_email || "",
    phone:    account.phone || "",
    website:  account.website || "",
    notes:    account.notes || "",
    location: account.location || "",
  };
  const [form, setForm]   = useState({ ...original });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: "" })); };

  const submit = async () => {
    const patch = {};
    if (form.name     !== original.name)     patch.name = form.name;
    if (form.type     !== original.type)     patch.customer_type = form.type;
    if (form.email    !== original.email)    patch.email = form.email;
    if (form.phone    !== original.phone)    patch.phone = form.phone;
    if (form.website  !== original.website)  patch.website = form.website;
    if (form.notes    !== original.notes)    patch.notes = form.notes;
    if (form.location !== original.location) patch.location = form.location;

    if (Object.keys(patch).length === 0) { toast.info("No changes to save"); onClose(); return; }
    if (!form.name.trim()) { setErrors({ name: "Account name is required" }); return; }

    setSaving(true);
    try {
      await api.patch(`/crm/accounts/${account.id}`, patch);
      toast.success("Account updated");
      onUpdated();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string") toast.error(detail);
      else toast.error(formatError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="bg-white rounded-xl border border-[#E5E5E5] w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-[#0176D3]" />
            <h2 className="text-base font-semibold text-[#181818]">Edit Account</h2>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-[#706E6B]" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Account Name <span className="text-rose-500">*</span></label>
            <input value={form.name} onChange={e => set("name", e.target.value)} data-testid="edit-account-name" className={`${inp}${errors.name ? " border-rose-400" : ""}`} />
            {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Account Type</label>
            <select value={form.type} onChange={e => set("type", e.target.value)} className={inp}>
              <option value="INDIVIDUAL">Individual</option>
              <option value="HOUSEHOLD">Household</option>
              <option value="ORGANIZATION">Organization</option>
              <option value="CORPORATE">Corporate</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="edit-account-email" className={inp} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Phone</label>
              <input value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="edit-account-phone" className={inp} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Location</label>
            <input value={form.location} onChange={e => set("location", e.target.value)} placeholder="City, State" className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Website</label>
            <input value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://…" className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} className={`${inp} resize-none`} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#706E6B] hover:text-[#181818]">Cancel</button>
          <button data-testid="edit-account-submit-btn" onClick={submit} disabled={saving} className="px-4 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#0060B0] disabled:opacity-50">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CRMAccounts() {  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [page,     setPage]     = useState(0);
  const [showCreate,  setShowCreate]  = useState(false);
  const [editAccount, setEditAccount] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/accounts", {
        params: {
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
          ...(search && { search }),
        },
      });
      setAccounts(data.results || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    const t = setTimeout(() => load(), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto space-y-5">
      {showCreate && (
        <CreateAccountModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {editAccount && (
        <EditAccountModal
          account={editAccount}
          onClose={() => setEditAccount(null)}
          onUpdated={() => { setEditAccount(null); load(); }}
        />
      )}
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-[#0176D3]" />
          <h1 className="text-xl font-bold text-[#181818] font-sans">Accounts</h1>
          <span className="text-xs font-mono text-[#706E6B] bg-[#F3F3F3] border border-[#E5E5E5] px-2 py-0.5 rounded">
            {total} total
          </span>
        </div>
        <button
          data-testid="new-account-btn"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-xs text-[#0176D3] hover:bg-[#EEF4FF] transition-colors font-medium"
        >
          <Plus className="w-3.5 h-3.5" /> New Account
        </button>
      </div>

      {/* Search */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4">
        <div className="relative max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search accounts…"
            data-testid="accounts-search"
            className="w-full pl-8 pr-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]/30"
          />
        </div>
      </div>

      {/* Table */}
      <div
        className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl overflow-hidden"
        data-testid="accounts-list"
      >
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#0176D3]" />
          </div>
        ) : accounts.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="No Accounts yet"
            note={
              search
                ? `No accounts matching "${search}"`
                : "Accounts will appear here when Leads are converted or Accounts are created."
            }
          />
        ) : (
          <>
            {/* ── Mobile card list ─────────────────────────────────────────── */}
            <div className="sm:hidden divide-y divide-[#E5E5E5]">
              {accounts.map(acc => (
                <div
                  key={acc.id}
                  data-testid="account-row"
                  onClick={() => navigate(`/crm/accounts/${acc.id}`)}
                  className="flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-[#EEF4FF] transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0176D3]/20 to-[#014486]/20 border border-[#E5E5E5] flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-[#0176D3]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#181818] truncate">{acc.name || acc.customer_name || "—"}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {(acc.customer_type || acc.type) && (
                        <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${TYPE_PILL[acc.customer_type || acc.type] || "text-[#706E6B] bg-slate-400/10 border-slate-400/20"}`}>
                          {acc.customer_type || acc.type}
                        </span>
                      )}
                      <span className="text-[10px] text-[#706E6B]">{acc.email || acc.contact_email || acc.location || "—"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      data-testid="edit-account-btn-mobile"
                      onClick={e => { e.stopPropagation(); setEditAccount(acc); }}
                      className="p-1.5 rounded text-[#706E6B] hover:text-[#0176D3] transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <ChevronRight className="w-4 h-4 text-[#706E6B]" />
                  </div>
                </div>
              ))}
            </div>
            {/* ── Desktop table ─────────────────────────────────────────────── */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E5E5] bg-white">
                    <Th>Account Name</Th>
                    <Th>Type</Th>
                    <Th className="hidden md:table-cell">Email / Location</Th>
                    <Th className="hidden lg:table-cell">Contacts</Th>
                    <Th className="hidden lg:table-cell">Leads</Th>
                    <Th className="hidden xl:table-cell">Created</Th>
                    <th className="px-4 py-3 w-8" />
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((acc, idx) => (
                    <tr
                      key={acc.id}
                      data-testid="account-row"
                      onClick={() => navigate(`/crm/accounts/${acc.id}`)}
                      className={`border-b border-[#E5E5E5] cursor-pointer hover:bg-[#EEF4FF] transition-colors ${idx % 2 !== 0 ? "bg-white/30" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#0176D3]/20 to-[#014486]/20 border border-[#E5E5E5] flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-3.5 h-3.5 text-[#0176D3]" />
                          </div>
                          <div>
                            <p className="text-[#181818] font-sans font-medium">
                              {acc.name || acc.customer_name || "—"}
                            </p>
                            {acc.account_number && (
                              <p className="text-[10px] font-mono text-[#706E6B]">{acc.account_number}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {acc.customer_type || acc.type ? (
                          <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${TYPE_PILL[acc.customer_type || acc.type] || "text-[#706E6B] bg-slate-400/10 border-slate-400/20"}`}>
                            {acc.customer_type || acc.type}
                          </span>
                        ) : <span className="text-[#706E6B] text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <p className="text-xs text-[#706E6B]">{acc.email || acc.contact_email || "—"}</p>
                        {(acc.location || acc.city) && (
                          <p className="text-[10px] text-[#706E6B]">{acc.location || acc.city}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="flex items-center gap-1 text-xs text-[#706E6B]">
                          <Users className="w-3 h-3" /> {acc.contact_count ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="flex items-center gap-1 text-xs text-[#706E6B]">
                          <LayoutList className="w-3 h-3" /> {acc.lead_count ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell">
                        <span className="text-xs text-[#706E6B]">{fmtDate(acc.created_at)}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            data-testid="edit-account-btn"
                            onClick={e => { e.stopPropagation(); setEditAccount(acc); }}
                            className="p-1.5 rounded text-[#706E6B] hover:text-[#0176D3] hover:bg-[#EEF4FF] transition-colors"
                            title="Edit account"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <ChevronRight className="w-4 h-4 text-[#706E6B]" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* ── end desktop table ───────────────────────────────────────── */}
            <Pagination page={page} total={total} size={PAGE_SIZE} onPage={setPage} />
          </>
        )}
      </div>
    </div>
  );
}

// ── Shared Primitives ─────────────────────────────────────────────────────────

function Th({ children, className = "" }) {
  return (
    <th className={`text-left px-4 py-3 text-xs font-mono text-[#706E6B] uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function EmptyState({ icon: Icon, title, note }) {
  return (
    <div className="py-16 text-center">
      <Icon className="w-8 h-8 text-[#444444] mx-auto mb-3" />
      <p className="text-sm text-[#706E6B] font-medium">{title}</p>
      {note && <p className="text-xs text-[#706E6B] mt-1 max-w-xs mx-auto">{note}</p>}
    </div>
  );
}

function Pagination({ page, total, size, onPage }) {
  if (total <= size) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E5E5]">
      <p className="text-xs text-[#706E6B]">
        Showing {page * size + 1}–{Math.min((page + 1) * size, total)} of {total}
      </p>
      <div className="flex gap-2">
        <button disabled={page === 0} onClick={() => onPage(p => p - 1)}
          className="px-3 py-1 text-xs border border-[#E5E5E5] rounded text-[#706E6B] hover:text-[#181818] hover:border-[#0176D3]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          Prev
        </button>
        <button disabled={(page + 1) * size >= total} onClick={() => onPage(p => p + 1)}
          className="px-3 py-1 text-xs border border-[#E5E5E5] rounded text-[#706E6B] hover:text-[#181818] hover:border-[#0176D3]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          Next
        </button>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" }); }
  catch { return "—"; }
}
